'use client';

import { memo, useCallback, useMemo, useState } from 'react';

import { type Clip } from '@air/api/types';
import { CheckIcon, ImageIcon } from '@air/design-icons';
import { DropdownMenuItem } from '@air/primitive-dropdown-menu';

import { VersionBadge } from '~/components/VersionBadge';
import { imgixLoader } from '~/utils/imgixLoader';

/** Format a date as "Jan 12, 2026" */
const formatShortDate = (date: Date | string) => {
  return new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(date));
};

export type VersionItemProps = {
  clip: Clip;
  isSelected: boolean;
  onSelect: () => void;
};

/** Rich dropdown item showing thumbnail, title, and metadata */
export const VersionItem = memo(({ clip, isSelected, onSelect }: VersionItemProps) => {
  const [imageError, setImageError] = useState(false);

  const thumbnailUrl = useMemo(() => {
    const src = clip.assets.image;

    if (!src) {
      return undefined;
    }

    return imgixLoader({ src, width: 80 });
  }, [clip.assets.image]);

  const title = clip.title ?? clip.importedName ?? 'Untitled';

  const meta = useMemo(() => {
    const parts: string[] = [];

    if (clip.ext) {
      parts.push(clip.ext.toUpperCase());
    }
    if (clip.createdAt) {
      parts.push(formatShortDate(clip.createdAt));
    }

    return parts.join(' · ');
  }, [clip.ext, clip.createdAt]);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const showPlaceholder = !thumbnailUrl || imageError;

  return (
    <DropdownMenuItem className="gap-2.5 py-1.5" onSelect={onSelect}>
      <div className="size-10 shrink-0 overflow-hidden rounded-4 ads:bg-subtle bg-gray-4">
        {showPlaceholder ? (
          <div className="flex size-full items-center justify-center ads:text-icon-subtle text-gray-8">
            <ImageIcon size="sm" />
          </div>
        ) : (
          // biome-ignore lint/performance/noImgElement: External CDN image
          <img
            alt={title}
            className="size-full object-cover"
            loading="lazy"
            onError={handleImageError}
            src={thumbnailUrl}
          />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <VersionBadge version={clip.version} />
          <p className="truncate font-medium ads:text-primary text-12 text-gray-12">{title}</p>
        </div>
        {meta && (
          <p className="mt-0.5 truncate ads:text-metadata text-11 text-gray-10 uppercase tracking-wider">{meta}</p>
        )}
      </div>

      {isSelected && <CheckIcon className="shrink-0 ads:text-icon-emphasis text-gray-12" size="xs" />}
    </DropdownMenuItem>
  );
});

VersionItem.displayName = 'VersionItem';
